/**
 * Inventory of the NourishedRx brand assets vendored under `./assets/`.
 *
 * @remarks
 * Before this manifest existed, logo files were duplicated into four app `public/`
 * directories with nothing guaranteeing they matched. This module is the single
 * declaration of what the brand ships; the design-skill build copies exactly these
 * files, and `asset-manifest.spec.ts` fails if a listed file is missing or an
 * unlisted file appears in `assets/`.
 *
 * Paths are relative to this file's directory so both Node build scripts and bundler
 * asset imports can resolve them.
 */

/** A logo lockup: the flower mark set alongside the NourishedRx wordmark. */
export interface BrandLockup {
  /** Path relative to `src/brand/`. */
  readonly path: string;
  /** Surface this variant is cleared for. */
  readonly usage: string;
}

/**
 * Full lockups. Multicolour is primary; the single-colour variants exist for
 * surfaces that cannot carry the full palette.
 */
export const BRAND_LOCKUPS: Readonly<Record<string, BrandLockup>> = {
  multicolorSvg: {
    path: 'assets/logo-multicolor.svg',
    usage: 'Primary. Vector — prefer this everywhere resolution is not fixed.',
  },
  multicolorPng: {
    path: 'assets/logo-multicolor.png',
    usage: 'Primary, raster fallback for contexts without SVG support.',
  },
  red: { path: 'assets/logo-red.png', usage: 'Single-colour coral on light backgrounds.' },
  white: { path: 'assets/logo-white.png', usage: 'Reversed, on forest or photography.' },
  black: { path: 'assets/logo-black.png', usage: 'Monochrome print and faxable documents.' },
} as const;

/**
 * Icon-only marks — the four-petal flower without the wordmark. Use below roughly
 * 120px wide, where the wordmark stops being legible.
 */
export const BRAND_MARKS: Readonly<Record<string, BrandLockup>> = {
  red: { path: 'assets/icon-red.png', usage: 'Favicons, avatars, app icons.' },
  white: { path: 'assets/icon-white.png', usage: 'Reversed on forest or coral.' },
  black: { path: 'assets/icon-black.png', usage: 'Monochrome print.' },
} as const;

/** Product photography and marketing imagery. */
export const BRAND_IMAGERY: Readonly<Record<string, BrandLockup>> = {
  platformHero: { path: 'assets/nrx-platform-hero.png', usage: 'Hero band, device stack.' },
  platform: { path: 'assets/nrx-platform.png', usage: 'Inline product shot.' },
  divider: { path: 'assets/divider.svg', usage: 'Decorative section rule.' },
} as const;

/**
 * Decorative flower motif variations, used as watermark, loader, and section accent.
 * These are illustration-weight; the UI-weight mark is the `FlowerMark` component.
 */
export const BRAND_ILLUSTRATIONS: readonly string[] = [
  'assets/illustrations/flower-1.svg',
  'assets/illustrations/flower-2.svg',
  'assets/illustrations/flower-3.svg',
  'assets/illustrations/flower-4.svg',
] as const;

/**
 * The proprietary two-point outline icon set, for brand artifacts — decks, mocks,
 * marketing pages.
 *
 * @remarks
 * This is NOT the product icon set. Application code uses Lucide through the
 * allowlist in `../icons.ts`, which is tree-shakeable and recolourable. These are
 * flat PNGs at a fixed colour, so they cannot follow `currentColor` or a theme
 * switch — which is precisely why product UI does not use them.
 */
export const BRAND_OUTLINE_ICONS: readonly string[] = [
  '001',
  '002',
  '003',
  '004',
  '005',
  '006',
  '007',
  '008',
  '009',
  '010',
  '011',
  '012',
  '013',
  '014',
  '015',
  '016',
  '017',
  '018',
  '019',
  '020',
  '021',
  '023',
  '025',
  '026',
  '027',
].map((n) => `assets/icons/icon-${n}.png`);

/** Every vendored brand asset path, for build copying and drift hashing. */
export function getAllBrandAssetPaths(): readonly string[] {
  return [
    ...Object.values(BRAND_LOCKUPS).map((a) => a.path),
    ...Object.values(BRAND_MARKS).map((a) => a.path),
    ...Object.values(BRAND_IMAGERY).map((a) => a.path),
    ...BRAND_ILLUSTRATIONS,
    ...BRAND_OUTLINE_ICONS,
  ];
}
